import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { InterestProfileStatistics } from '../flyout-models';

declare let Chart: any;

@Component({
  selector: 'app-flyout-interest-profile-chart',
  templateUrl: './flyout-interest-profile-chart.component.html',
  styleUrls: ['./flyout-interest-profile-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutInterestProfileChartComponent implements OnInit, OnDestroy {
  private readonly logger: Logger;
  @Input() public height = 120;
  @ViewChild('chartCanvas', { static: true }) chartCanvasRef: ElementRef;
  private myChart;
  private interestProfiles: Array<InterestProfileStatistics>;

  public dataSetConfigs = {
    open: {
      labelKey: 'campaign.analytics.count',
      lineColor: 'rgba(0, 176, 255, 1)',
      backgroundColor: 'rgba(0, 176, 255, 0.2)',
    },
    conversion: {
      labelKey: 'campaign.analytics.conversions',
      lineColor: '#F86DB6',
      backgroundColor: '#F86DB6',
    },
    clickthrough: {
      labelKey: 'campaign.analytics.click-throughs',
      lineColor: '#FFA8D7',
      backgroundColor: '#FFA8D7',
    },
  };

  @Input()
  public set dataArray(interestProfiles: Array<InterestProfileStatistics>) {
    this.interestProfiles = interestProfiles;
    if (interestProfiles) {
      this.renderChart();
    } else {
      if (this.myChart !== undefined) {
        this.myChart.destroy();
      }
      this.chartCanvasRef.nativeElement.innerHTML = '';
    }
  }

  constructor(public loggerFactory: LoggerFactory) {
    this.logger = loggerFactory.get('FlyoutInterestProfileChartComponent');
  }

  ngOnInit() {
    this.renderChart();
  }

  ngOnDestroy() {
    this.chartCanvasRef.nativeElement.innerHTML = '';
    if (this.myChart != null) {
      this.myChart.destroy();
    }
  }

  // private generateDataSet(
  //   data: Array<any>,
  //   config: { labelKey: string; lineColor: string; backgroundColor: string },
  //   axisId: string,
  //   fill = true,
  //   type?: string
  // ) {
  //   return {
  //     label: this.languageService.translateInstantly(config.labelKey),
  //     hidden: false,
  //     data: data,
  //     borderColor: fill ? config.backgroundColor : config.lineColor,
  //     backgroundColor: config.backgroundColor,
  //     fill: fill,
  //     borderWidth: 2,
  //     pointRadius: 2,
  //     pointBackgroundColor: '#fff',
  //     lineTension: 0.4,
  //     yAxisID: axisId,
  //     type: type,
  //   };
  // }

  private renderChart() {
    const dataSettings = { labels: [], datasets: [] };
    let options;

    const backgroundColors = [
      'rgba(90, 172, 208, 0.5)',
      'rgba(242,142,237, 0.5)',
      'rgba(255,177,21, 0.5)',
      'rgba(33,226,232, 0.5)',
      'rgba(125, 206, 130, 0.5)',
      'rgba(67, 103, 63, 0.5)',
      'rgba(245, 145, 0, 0.5)',
      'rgba(255, 113, 188, 0.5)',
    ];
    const hoverBackgroundColor = [
      'rgba(90, 172, 208, 1)',
      'rgba(242,142,237, 1)',
      'rgba(255,177,21, 1)',
      'rgba(33,226,232, 1)',
      'rgba(125, 206, 130, 1)',
      'rgba(67, 103, 63, 1)',
      'rgba(245, 145, 0, 1)',
      'rgba(255, 113, 188, 1)',
    ];

    const datasetData = [];
    for (let i = 0; i < this.interestProfiles.length; i++) {
      const interestProfile = this.interestProfiles[i];
      // Labels
      if (dataSettings.labels.indexOf(interestProfile.name) === -1) {
        dataSettings.labels.push(interestProfile.name);
      }

      // Data
      datasetData.push(interestProfile.value);
    }

    dataSettings.datasets.push({
      data: datasetData,
      backgroundColor: backgroundColors,
      hoverBackgroundColor: hoverBackgroundColor,
    });

    options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            boxWidth: 11,
            pointStyle: 'triangle',
            padding: 3,
            color: '#999',
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const label = ' ' + context.raw + '%';
              return label;
            },
          },
        },
      },
    };

    // Get canvas
    const canvas = <HTMLCanvasElement>this.chartCanvasRef.nativeElement;
    if (!canvas) {
      this.logger.error('Canvas not found');
      return;
    }
    const ctx = canvas.getContext('2d');

    // Destroy old chart if it exists
    if (this.myChart != null) {
      this.myChart.destroy();
    }

    this.renderChartWhenLoaded(ctx, dataSettings, options);
  }

  renderChartWhenLoaded(ctx: CanvasRenderingContext2D, dataSettings: any, options: any) {
    // Init component
    this.myChart = new Chart(ctx, {
      type: 'doughnut',
      data: dataSettings,
      options: options,
    });
    // Set default fontsize
    Chart.defaults.plugins.legend.maxHeight = 32;
    Chart.defaults.font.size = 11;
  }

  onDownloadAsImageClicked() {
    const canvas = <HTMLCanvasElement>this.chartCanvasRef.nativeElement;
    const imageUrl = canvas.toDataURL('image/png');
    const filename = this.getNameOfChart();
    this.createDownloadLink(imageUrl, filename + '.png');
  }

  onDownloadAsJsonClicked() {
    const filename = this.getNameOfChart();
    this.createDownloadLink('data:plain/text,' + JSON.stringify(this.interestProfiles), filename + '.json');
  }

  createDownloadLink(href, filename) {
    // We need an acctual a-tag to be able to set the filename of image. We add -> click and then remove it
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getNameOfChart() {
    return 'chart';
  }

  // private formatDate(dateString) {
  //   const date = new Date(dateString);
  //   return date.getDate() + ' ' + this.months[date.getMonth()];
  // }

  // private formatNumbers(numberString) {
  //   if (!numberString) {
  //     return numberString;
  //   }
  //   const x = numberString.toString().split('.');
  //   let x1 = x[0];
  //   const x2 = x.length > 1 ? '.' + x[1] : '';
  //   const rgx = /(\d+)(\d{3})/;
  //   while (rgx.test(x1)) {
  //     x1 = x1.replace(rgx, '$1' + ' ' + '$2');
  //   }
  //   return x1 + x2;
  // }
}
